import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { PageContainer } from '@/components/page/PageContainer';
import { PageSizer, SizerMode } from '@/components/page/PageSizer';
import { TSCard } from '@/components/shared/card/Card';
import { DayPicker } from '@/components/shared/date/DayPicker';
import { TableLoader } from '@/components/shared/table/TableLoader';
import { dateToApiFormat, getTomorrowUtc } from '@/helpers/dateHelper';
import { merge } from '@/helpers/Utility';
import { useSystemSettings } from '@/services/settings/SystemSettingsProvider';
import { FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import strings from '../../ess.translations';
import { MyAvailabilityContent } from './MyAvailabilityContent';
import { MyAvailabilityHeader } from './MyAvailabilityHeader';

const MyAvailability: FunctionComponent = () => {
  const { date } = useParams<{ date: string }>();
  const navigate = useNavigate();
  const allowEssAvailabilityAdjustment = useSystemSettings(state => state.settings.allowEssAvailabilityAdjustment);

  if (!date) {
    throw new Error('Date parameter is required');
  }

  const minDate = useMemo(getTomorrowUtc, []);
  const dateObj = useMemo(() => new Date(date), [date]);
  const isSelectable = dateObj >= minDate;

  const [selectedDate, setSelectedDate] = useState<Date>(isSelectable ? dateObj : minDate);

  const goToDate = (d: Date) => navigate(`/ess/my-calendar/${dateToApiFormat(d)}/my-availability`, { replace: true });

  useEffect(() => {
    if (isSelectable) {
      setSelectedDate(dateObj);
    } else {
      setSelectedDate(minDate);
      goToDate(minDate);
    }
  }, [dateObj]);

  const onChangeDate = (newDate: Date) => {
    if (newDate < minDate) return;
    setSelectedDate(newDate);
    goToDate(newDate);
  };

  const dateKey = dateToApiFormat(selectedDate);

  if (!allowEssAvailabilityAdjustment) {
    return (
      <PageContainer customHeader={<MyAvailabilityHeader />}>
        <PageSizer mode={SizerMode.PAGE}>
          <TSCard className="p-8 text-center">{strings.myAvailability.featureDisabled}</TSCard>
        </PageSizer>
      </PageContainer>
    );
  }

  return (
    <PageContainer customHeader={<MyAvailabilityHeader />}>
      <div className="bg-white">
        <PageSizer mode={SizerMode.PAGE}>
          <DayPicker value={selectedDate} onChange={onChangeDate} minDate={minDate} />
        </PageSizer>
      </div>
      <div className="overflow-y-hidden flex flex-col bg-white">
        <PageSizer mode={SizerMode.PAGE} className="flex-1 overflow-auto">
          <SubmissionErrorBoundary>
            <TSCard className={merge('p-4 border-0 lg:border')}>
              <Suspense fallback={<TableLoader />}>
                <MyAvailabilityContent
                  key={dateKey}
                  date={selectedDate}
                  onSaved={() => navigate('change-requested')}
                  onCancel={() => navigate(-1)}
                />
              </Suspense>
            </TSCard>
          </SubmissionErrorBoundary>
        </PageSizer>
      </div>
    </PageContainer>
  );
};

export default MyAvailability;
